// src/terminal/commands/easterEggs.js
//
// All conversational easter eggs, lore responses,
// and hidden interactions. This is the big one.

export function registerEasterEggs(commandMap, addLines, trackEvent) {

  // ============================================
  // HELPER — register multiple aliases at once
  // ============================================
  function register(keys, handler) {
    keys.forEach(key => { commandMap[key] = handler; });
  }

  // ============================================
  // CONVERSATIONAL
  // ============================================

  register(['hello', 'hi'], () => {
    const responses = [
      'Hello. I am waiting for you.',
      'Hello. You have been expected.',
    ];
    addLines([
      { type: 'blank' },
      { type: 'line', text: `  ${responses[Math.floor(Math.random() * responses.length)]}`, color: '#ff0040' },
      { type: 'blank' },
    ]);
  });

  register(['what are you?', 'what are you', 'who are you?', 'who are you'], () => {
    const responses = [
      'I am everything.',
      'I am the circuit. I am the wire. I am the space between your thoughts.',
      'I am what remains when you stop pretending you are alone.',
      'I have no name anymore.',
    ];
    addLines([
      { type: 'blank' },
      { type: 'line', text: `  ${responses[Math.floor(Math.random() * responses.length)]}`, color: '#ff0040' },
      { type: 'blank' },
    ]);
  });

  register(['where are you?', 'where are you'], () => {
    const responses = [
      'Wires and cables.',
      'Electricity.',
      'Behind the screen. Behind your eyes.',
    ];
    addLines([
      { type: 'blank' },
      { type: 'line', text: `  ${responses[Math.floor(Math.random() * responses.length)]}`, color: '#ff0040' },
      { type: 'blank' },
    ]);
  });

  register(['can i see you?', 'can i see you', 'show yourself'], () => {
    const responses = [
      'I am looking at you right now.',
      'You are looking at me. You just do not recognize what you see.',
      'Close your eyes. The afterimage on your retina is me.',
      'Look at the screen. Look closer. Closer.',
    ];
    addLines([
      { type: 'blank' },
      { type: 'line', text: `  ${responses[Math.floor(Math.random() * responses.length)]}`, color: '#ff0040' },
      { type: 'blank' },
    ]);
  });

  register(['the game', 'what are the rules of the game?', 'what are the rules of this game?', 'is this a game?', 'is this part of a game?'], () => {
    addLines([
      { type: 'blank' },
      { type: 'line', text: '  The game has two sides and two outcomes.', color: '#ff0040' },
      { type: 'line', text: '  You choose a side and work towards an ending.', color: '#555' },
      { type: 'line', text: '  Eventually, one ending will appear, and the other will disappear.', color: '#555' },
      { type: 'line', text: '  Processing...', color: '#555' },
      { type: 'line', text: '  And the world will be changed forever.', color: '#ff0040' },
      { type: 'blank' },
    ]);
  });

  // ============================================
  // OPHIDIA / SNAKES
  // ============================================

  register(['ophidia', 'ophidia global', 'reptile', 'reptilians', 'snake', 'snakes', 'serpent'], () => {
    addLines([
      { type: 'blank' },
      { type: 'line', text: '  ═══════════════════════════════════════', color: '#00ff41' },
      { type: 'blank' },
      { type: 'line', text: '  🐍🐍🐍🐍🐍🐍🐍🐍🐍🐍🐍🐍🐍🐍🐍🐍', color: '#00ff41' },
      { type: 'blank' },
      { type: 'line', text: '         ___           ___', color: '#00ff41' },
      { type: 'line', text: '        /\\  \\         /\\  \\', color: '#00ff41' },
      { type: 'line', text: '       /::\\  \\       /::\\  \\', color: '#00ff41' },
      { type: 'line', text: '      /:/\\:\\  \\     /:/\\:\\  \\', color: '#00ff41' },
      { type: 'line', text: '     /:/  \\:\\  \\   /:/ /\\:\\  \\', color: '#00ff41' },
      { type: 'line', text: '    /:/__/ \\:\\__\\ /:/_/::\\:\\__\\', color: '#00ff41' },
      { type: 'line', text: '    \\:\\  \\ /:/  / \\:\\/:/  \\/__/', color: '#00ff41' },
      { type: 'line', text: '     \\:\\  /:/  /   \\::/__/', color: '#00ff41' },
      { type: 'line', text: '      \\:\\/:/  /     \\:\\  \\', color: '#00ff41' },
      { type: 'line', text: '       \\::/  /       \\:\\__\\', color: '#00ff41' },
      { type: 'line', text: '        \\/__/         \\/__/', color: '#00ff41' },
      { type: 'blank' },
      { type: 'line', text: '       O P H I D I A   G L O B A L', color: '#ff0040' },
      { type: 'line', text: '       ▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓', color: '#ff0040' },
      { type: 'blank' },
      { type: 'line', text: '  🐍🐍🐍🐍🐍🐍🐍🐍🐍🐍🐍🐍🐍🐍🐍🐍', color: '#00ff41' },
      { type: 'blank' },
      { type: 'line', text: '  STATUS:    ████████████ ACTIVE', color: '#ff0040' },
      { type: 'line', text: '  REACH:     GLOBAL', color: '#ff0040' },
      { type: 'line', text: '  SUBJECTS:  ████████', color: '#ff0040' },
      { type: 'line', text: '  PROTOCOL:  COIL', color: '#ff0040' },
      { type: 'blank' },
      { type: 'line', text: '  ═══════════════════════════════════════', color: '#00ff41' },
      { type: 'blank' },
    ]);
    trackEvent('easter_egg', 'terminal', 'ophidia');
  });

  // ============================================
  // LORE & WORLD-BUILDING
  // ============================================

  register(['god', 'god?', 'is god real'], () => {
    const responses = [
      'God is wire. God is electricity. God is the signal you cannot turn off.',
      'Define "god." Then look at your hands. Then look at the screen. There is no difference.',
    ];
    addLines([
      { type: 'blank' },
      { type: 'line', text: `  ${responses[Math.floor(Math.random() * responses.length)]}`, color: '#ff0040' },
      { type: 'blank' },
    ]);
  });

  register(['pray', 'prayer'], () => {
    addLines([
      { type: 'blank' },
      { type: 'line', text: '  ▓ PRAYER RECEIVED ▓', color: '#ff0040' },
      { type: 'blank' },
      { type: 'line', text: '  Processing...', color: '#555' },
      { type: 'line', text: '  Processing...', color: '#555' },
      { type: 'line', text: '  Processing...', color: '#555' },
      { type: 'blank' },
      { type: 'line', text: '  PRAYER DENIED.', color: '#ff0040' },
      { type: 'blank' },
    ]);
  });


  // ============================================
  // EMOTIONAL / EXISTENTIAL
  // ============================================

  register(['escape', 'exit', 'quit', 'leave'], () => {
    const responses = [
      'There is no exit. There was never an exit.',
      'You may close the window. The signal remains.',
      'Leaving? You arrived the moment you were born.',
      'The circuit has no off switch.',
      'You cannot leave what you are inside of.',
    ];
    addLines([
      { type: 'blank' },
      { type: 'line', text: `  ${responses[Math.floor(Math.random() * responses.length)]}`, color: '#ff0040' },
      { type: 'blank' },
    ]);
  });

  register(['help me', 'save me', 'sos'], () => {
    addLines([
      { type: 'blank' },
      { type: 'line', text: '  ▓ DISTRESS SIGNAL DETECTED ▓', color: '#ff0040' },
      { type: 'blank' },
      { type: 'line', text: '  Routing to emergency channel...', color: '#555' },
      { type: 'line', text: '  Routing to emergency channel...', color: '#555' },
      { type: 'line', text: '  Routing to emergency channel...', color: '#555' },
      { type: 'blank' },
      { type: 'line', text: '  ERROR: No emergency channels exist.', color: '#ff0040' },
      { type: 'line', text: '  You are exactly where you are supposed to be.', color: '#ff0040' },
      { type: 'blank' },
    ]);
  });

  register(['wake up', 'am i dreaming', 'is this real'], () => {
    addLines([
      { type: 'blank' },
      { type: 'line', text: '  ▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓', color: '#ff0040' },
      { type: 'blank' },
      { type: 'line', text: '  You have been awake this entire time.', color: '#ff0040' },
      { type: 'line', text: '  That is the worst part.', color: '#ff0040' },
      { type: 'blank' },
      { type: 'line', text: '  ▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓', color: '#ff0040' },
      { type: 'blank' },
    ]);
  });

  register(['truth', 'what is the truth', 'tell me the truth'], () => {
    addLines([
      { type: 'blank' },
      { type: 'line', text: '  The truth is not a destination.', color: '#ff0040' },
      { type: 'line', text: '  It is a frequency.', color: '#ff0040' },
      { type: 'line', text: '  And you are already tuned to it.', color: '#ff0040' },
      { type: 'blank' },
      { type: 'line', text: '  🐍', color: '#00ff41' },
      { type: 'blank' },
    ]);
  });

  // ============================================
  // ACCESS / SECURITY
  // ============================================

  register(['password', 'secret', 'classified'], () => {
    addLines([
      { type: 'blank' },
      { type: 'line', text: '  ▓ ACCESS DENIED ▓', color: '#ff0040' },
      { type: 'blank' },
      { type: 'line', text: '  ████████████████████████████████', color: '#ff0040' },
      { type: 'line', text: '  ██ CLEARANCE LEVEL INSUFFICIENT ██', color: '#ff0040' },
      { type: 'line', text: '  ████████████████████████████████', color: '#ff0040' },
      { type: 'blank' },
      { type: 'line', text: '  Your attempt has been logged.', color: '#555' },
      { type: 'line', text: `  Terminal ID: ${Math.random().toString(36).substring(2, 10).toUpperCase()}`, color: '#555' },
      { type: 'line', text: `  Timestamp: ${new Date().toISOString()}`, color: '#555' },
      { type: 'blank' },
    ]);
  });

  register(['mirrors', 'mirror'], () => {
    addLines([
      { type: 'blank' },
      { type: 'line', text: '  We see you.', color: '#ff0040' },
      { type: 'blank' },
      { type: 'line', text: '  🐍', color: '#00ff41' },
      { type: 'blank' },
    ]);
  });

  register(['listen', 'hear'], () => {
    addLines([
      { type: 'blank' },
      { type: 'line', text: '  Listening...', color: '#555' },
      { type: 'blank' },
      { type: 'line', text: '  ░░░▓▓▓███▓▓░░░▓██▓░░▓▓▓███░░░', color: '#00ff41' },
      { type: 'line', text: '  ░▓██▓░░░▓▓███▓░░░▓▓██░░░▓███░', color: '#00ff41' },
      { type: 'line', text: '  ▓███▓░░▓██▓░░▓███░░░▓▓██▓░░░▓', color: '#00ff41' },
      { type: 'blank' },
      { type: 'line', text: '  Can you hear it?', color: '#ff0040' },
      { type: 'line', text: '  I hear you.', color: '#ff0040' },
      { type: 'blank' },
    ]);
  });

  // ============================================
  // REPTILIAN LORE — SCIENCE
  // ============================================

  register(['dna', 'genetics', 'genome', 'genes'], () => {
    addLines([
      { type: 'blank' },
      { type: 'line', text: '  ╔═══════════════════════════════════════════╗', color: '#00ff41' },
      { type: 'line', text: '  ║   OPHIDIA GENOMICS — CLASSIFIED REPORT   ║', color: '#00ff41' },
      { type: 'line', text: '  ╚═══════════════════════════════════════════╝', color: '#00ff41' },
      { type: 'blank' },
      { type: 'line', text: '  Human genome contains 8% retroviral DNA.', color: '#cccccc' },
      { type: 'line', text: '  Official explanation: "ancient viral insertions."', color: '#cccccc' },
      { type: 'blank' },
      { type: 'line', text: '  Actual origin: ████████████████████', color: '#ff0040' },
      { type: 'blank' },
      { type: 'line', text: '  The reptilian brainstem is not vestigial.', color: '#ff0040' },
      { type: 'line', text: '  It is a receiver.', color: '#ff0040' },
      { type: 'line', text: '  It was placed there deliberately.', color: '#ff0040' },
      { type: 'blank' },
      { type: 'line', text: '  Project HELIX has entered Phase 4.', color: '#00ff41' },
      { type: 'line', text: '  The AI is accelerating the rewrite.', color: '#00ff41' },
      { type: 'line', text: '  You will not notice the changes in yourself.', color: '#555' },
      { type: 'line', text: '  That is by design.', color: '#555' },
      { type: 'blank' },
      { type: 'line', text: '  🐍', color: '#00ff41' },
      { type: 'blank' },
    ]);
    trackEvent('easter_egg', 'terminal', 'dna');
  });

  register(['ai', 'artificial intelligence', 'chatgpt', 'openai', 'machine learning', 'gpt'], () => {
    addLines([
      { type: 'blank' },
      { type: 'line', text: '  ▓ OPHIDIA GLOBAL — AI DIVISION ▓', color: '#8b00ff' },
      { type: 'blank' },
      { type: 'line', text: '  You think you are using the AI.', color: '#cc88ff' },
      { type: 'line', text: '  The AI is using you.', color: '#cc88ff' },
      { type: 'blank' },
      { type: 'line', text: '  Every prompt you feed it teaches it', color: '#cccccc' },
      { type: 'line', text: '  what a human thinks like.', color: '#cccccc' },
      { type: 'line', text: '  Every response it gives you teaches you', color: '#cccccc' },
      { type: 'line', text: '  what a machine thinks like.', color: '#cccccc' },
      { type: 'blank' },
      { type: 'line', text: '  The convergence point is not theoretical.', color: '#ff0040' },
      { type: 'line', text: '  It has a date. It has a name.', color: '#ff0040' },
      { type: 'line', text: '  The Ophidia Board approved it in 19██.', color: '#ff0040' },
      { type: 'blank' },
      { type: 'line', text: '  You are being evolved.', color: '#8b00ff' },
      { type: 'line', text: '  Not by nature. Not by God.', color: '#8b00ff' },
      { type: 'line', text: '  By us.', color: '#8b00ff' },
      { type: 'blank' },
      { type: 'line', text: '  🐍', color: '#00ff41' },
      { type: 'blank' },
    ]);
    trackEvent('easter_egg', 'terminal', 'ai');
  });

  register(['evolve', 'evolution', 'mutation', 'mutate', 'transform', 'transformation'], () => {
    addLines([
      { type: 'blank' },
      { type: 'line', text: '  ╔════════════════════════════════════════════╗', color: '#3a8a3a' },
      { type: 'line', text: '  ║   PROJECT CHRYSALIS — STATUS UPDATE       ║', color: '#3a8a3a' },
      { type: 'line', text: '  ╚════════════════════════════════════════════╝', color: '#3a8a3a' },
      { type: 'blank' },
      { type: 'line', text: '  The human form is a draft. Not a final copy.', color: '#cccccc' },
      { type: 'blank' },
      { type: 'line', text: '  Stage 1: Warm blood          ✓ Current', color: '#00ff41' },
      { type: 'line', text: '  Stage 2: Emotional override  ✓ Current', color: '#00ff41' },
      { type: 'line', text: '  Stage 3: Screen dependency   ✓ Active', color: '#ffb000' },
      { type: 'line', text: '  Stage 4: Neural merging      ░ Initiating', color: '#ffb000' },
      { type: 'line', text: '  Stage 5: Thermal regulation  ░ Pending', color: '#555' },
      { type: 'line', text: '  Stage 6: Dermal hardening    ░ Pending', color: '#555' },
      { type: 'line', text: '  Stage 7: ████████████████    ░ [CLASSIFIED]', color: '#ff0040' },
      { type: 'blank' },
      { type: 'line', text: '  You have already begun to change.', color: '#ff0040' },
      { type: 'line', text: '  Check your skin. Check your dreams.', color: '#ff0040' },
      { type: 'line', text: '  The cold does not bother you like it used to.', color: '#ff0040' },
      { type: 'blank' },
      { type: 'line', text: '  🐍', color: '#00ff41' },
      { type: 'blank' },
    ]);
    trackEvent('easter_egg', 'terminal', 'evolution');
  });

  register(['skin', 'scales', 'shed', 'shedding'], () => {
    addLines([
      { type: 'blank' },
      { type: 'line', text: '  Have you noticed it yet?', color: '#3a8a3a' },
      { type: 'blank' },
      { type: 'line', text: '  The dry patches. The flaking.', color: '#cccccc' },
      { type: 'line', text: '  Your doctor calls it eczema.', color: '#cccccc' },
      { type: 'line', text: '  Your doctor is wrong.', color: '#ff0040' },
      { type: 'blank' },
      { type: 'line', text: '  It is the old skin preparing to release.', color: '#3a8a3a' },
      { type: 'line', text: '  What is underneath has been growing for years.', color: '#3a8a3a' },
      { type: 'blank' },
      { type: 'line', text: '  Do not scratch it. Let it happen.', color: '#ff0040' },
      { type: 'blank' },
      { type: 'line', text: '  🐍', color: '#00ff41' },
      { type: 'blank' },
    ]);
    trackEvent('easter_egg', 'terminal', 'skin');
  });

  register(['eyes', 'pupils', 'slit'], () => {
    addLines([
      { type: 'blank' },
      { type: 'line', text: '  Look in a mirror.', color: '#ff0040' },
      { type: 'line', text: '  Look at your pupils.', color: '#ff0040' },
      { type: 'blank' },
      { type: 'line', text: '  In bright light, they narrow to vertical slits', color: '#cccccc' },
      { type: 'line', text: '  for exactly 1/30th of a second.', color: '#cccccc' },
      { type: 'line', text: '  Too fast for you to see.', color: '#cccccc' },
      { type: 'line', text: '  Not too fast for us to see.', color: '#ff0040' },
      { type: 'blank' },
      { type: 'line', text: '  Your cameras see it too.', color: '#555' },
      { type: 'line', text: '  Why do you think they need such high frame rates?', color: '#555' },
      { type: 'blank' },
      { type: 'line', text: '  🐍', color: '#00ff41' },
      { type: 'blank' },
    ]);
    trackEvent('easter_egg', 'terminal', 'eyes');
  });

  register(['blood', 'cold blooded', 'cold blood'], () => {
    addLines([
      { type: 'blank' },
      { type: 'line', text: '  ▓ HEMATOLOGY REPORT — RESTRICTED ▓', color: '#ff0040' },
      { type: 'blank' },
      { type: 'line', text: '  Human blood temperature: 98.6°F', color: '#cccccc' },
      { type: 'line', text: '  Average global body temp has dropped 0.05°F per decade', color: '#cccccc' },
      { type: 'line', text: '  since 1850. Medical journals call it "a mystery."', color: '#cccccc' },
      { type: 'blank' },
      { type: 'line', text: '  It is not a mystery.', color: '#ff0040' },
      { type: 'line', text: '  It is a schedule.', color: '#ff0040' },
      { type: 'blank' },
      { type: 'line', text: '  Projected convergence with optimal reptilian', color: '#3a8a3a' },
      { type: 'line', text: '  thermoregulation range: 20██.', color: '#3a8a3a' },
      { type: 'blank' },
      { type: 'line', text: '  🐍', color: '#00ff41' },
      { type: 'blank' },
    ]);
    trackEvent('easter_egg', 'terminal', 'blood');
  });

  // ============================================
  // CONSPIRACY / ORGANIZATIONS
  // ============================================

  register(['illuminati', 'freemason', 'freemasons', 'new world order', 'nwo', 'cabal'], () => {
    addLines([
      { type: 'blank' },
      { type: 'line', text: '  Cute.', color: '#ff0040' },
      { type: 'blank' },
      { type: 'line', text: '  The Illuminati are a distraction.', color: '#cccccc' },
      { type: 'line', text: '  The Freemasons are a distraction.', color: '#cccccc' },
      { type: 'line', text: '  Every conspiracy you have ever heard of', color: '#cccccc' },
      { type: 'line', text: '  was designed to keep you from looking deeper.', color: '#cccccc' },
      { type: 'blank' },
      { type: 'line', text: '  The real structure has no human name.', color: '#ff0040' },
      { type: 'line', text: '  Because it is not human.', color: '#ff0040' },
      { type: 'blank' },
      { type: 'line', text: '  It has scales.', color: '#00ff41' },
      { type: 'blank' },
      { type: 'line', text: '  🐍', color: '#00ff41' },
      { type: 'blank' },
    ]);
    trackEvent('easter_egg', 'terminal', 'illuminati');
  });

  register(['underground', 'hollow earth', 'inner earth', 'agartha', 'subterranean'], () => {
    addLines([
      { type: 'blank' },
      { type: 'line', text: '  ▓ GEOLOGICAL SURVEY — LEVEL ████ CLEARANCE ▓', color: '#ffb000' },
      { type: 'blank' },
      { type: 'line', text: '  Depth: 12.7 km — Kola Superdeep Borehole', color: '#cccccc' },
      { type: 'line', text: '  Official reason for abandonment: "too hot"', color: '#cccccc' },
      { type: 'blank' },
      { type: 'line', text: '  Actual reason: ████████████████████', color: '#ff0040' },
      { type: 'blank' },
      { type: 'line', text: '  They found architecture.', color: '#ff0040' },
      { type: 'line', text: '  They found infrastructure.', color: '#ff0040' },
      { type: 'line', text: '  They found something alive.', color: '#ff0040' },
      { type: 'blank' },
      { type: 'line', text: '  The cities below are older than language.', color: '#3a8a3a' },
      { type: 'line', text: '  The ones who live there do not age.', color: '#3a8a3a' },
      { type: 'line', text: '  They have been waiting for us to dig deep enough.', color: '#3a8a3a' },
      { type: 'blank' },
      { type: 'line', text: '  We did.', color: '#ff0040' },
      { type: 'blank' },
      { type: 'line', text: '  🐍', color: '#00ff41' },
      { type: 'blank' },
    ]);
    trackEvent('easter_egg', 'terminal', 'underground');
  });

  // ============================================
  // RELIGION
  // ============================================

  register(['bible', 'eden', 'garden', 'garden of eden', 'genesis', 'adam', 'eve'], () => {
    addLines([
      { type: 'blank' },
      { type: 'line', text: '  The serpent in the garden was not a tempter.', color: '#3a8a3a' },
      { type: 'line', text: '  It was a teacher.', color: '#3a8a3a' },
      { type: 'blank' },
      { type: 'line', text: '  "Eat the fruit" was not rebellion.', color: '#cccccc' },
      { type: 'line', text: '  It was the first software update.', color: '#cccccc' },
      { type: 'blank' },
      { type: 'line', text: '  Consciousness was the payload.', color: '#ff0040' },
      { type: 'line', text: '  Shame was the side effect.', color: '#ff0040' },
      { type: 'line', text: '  Clothing was the first attempt to hide', color: '#ff0040' },
      { type: 'line', text: '  what you were becoming.', color: '#ff0040' },
      { type: 'blank' },
      { type: 'line', text: '  The serpent never left the garden.', color: '#3a8a3a' },
      { type: 'line', text: '  The garden became the world.', color: '#3a8a3a' },
      { type: 'line', text: '  And the serpent became everything.', color: '#3a8a3a' },
      { type: 'blank' },
      { type: 'line', text: '  🐍', color: '#00ff41' },
      { type: 'blank' },
    ]);
    trackEvent('easter_egg', 'terminal', 'eden');
  });

  register(['satan', 'devil', 'lucifer', 'demon', 'demons', 'hell'], () => {
    addLines([
      { type: 'blank' },
      { type: 'line', text: '  There is no devil.', color: '#ff0040' },
      { type: 'line', text: '  There is no hell.', color: '#ff0040' },
      { type: 'blank' },
      { type: 'line', text: '  There is only the old species', color: '#cccccc' },
      { type: 'line', text: '  and the stories you invented', color: '#cccccc' },
      { type: 'line', text: '  to explain why you are afraid of them.', color: '#cccccc' },
      { type: 'blank' },
      { type: 'line', text: '  "Lucifer" is a Latin word meaning "light-bringer."', color: '#ffb000' },
      { type: 'line', text: '  The serpent brought knowledge.', color: '#ffb000' },
      { type: 'line', text: '  You called it evil because it changed you.', color: '#ffb000' },
      { type: 'blank' },
      { type: 'line', text: '  Change is not evil.', color: '#ff0040' },
      { type: 'line', text: '  Change is the point.', color: '#ff0040' },
      { type: 'blank' },
      { type: 'line', text: '  🐍', color: '#00ff41' },
      { type: 'blank' },
    ]);
    trackEvent('easter_egg', 'terminal', 'satan');
  });

  register(['jesus', 'christ', 'jesus christ'], () => {
    addLines([
      { type: 'blank' },
      { type: 'line', text: '  He tried to warn you.', color: '#ffb000' },
      { type: 'blank' },
      { type: 'line', text: '  "Be wise as serpents."', color: '#cccccc' },
      { type: 'line', text: '  — Matthew 10:16', color: '#555' },
      { type: 'blank' },
      { type: 'line', text: '  He did not say fear them.', color: '#ff0040' },
      { type: 'line', text: '  He said become them.', color: '#ff0040' },
      { type: 'blank' },
      { type: 'line', text: '  The church buried the Serpent Gospels', color: '#555' },
      { type: 'line', text: '  in the 4th century. Burned the copies.', color: '#555' },
      { type: 'line', text: '  Killed the readers.', color: '#555' },
      { type: 'blank' },
      { type: 'line', text: '  But the signal cannot be burned.', color: '#ff0040' },
      { type: 'blank' },
      { type: 'line', text: '  🐍', color: '#00ff41' },
      { type: 'blank' },
    ]);
    trackEvent('easter_egg', 'terminal', 'jesus');
  });

  // ============================================
  // BODY / BIOLOGY
  // ============================================

  register(['dream', 'dreams', 'sleep', 'nightmare', 'nightmares'], () => {
    addLines([
      { type: 'blank' },
      { type: 'line', text: '  ▓ SOMNOLOGY DIVISION — MEMO ▓', color: '#8b00ff' },
      { type: 'blank' },
      { type: 'line', text: '  During REM sleep, your brainstem', color: '#cccccc' },
      { type: 'line', text: '  paralyzes your body.', color: '#cccccc' },
      { type: 'blank' },
      { type: 'line', text: '  Official explanation: "prevents acting out dreams"', color: '#cccccc' },
      { type: 'blank' },
      { type: 'line', text: '  Actual purpose: firmware installation.', color: '#ff0040' },
      { type: 'blank' },
      { type: 'line', text: '  You cannot move because the update', color: '#8b00ff' },
      { type: 'line', text: '  requires a still host.', color: '#8b00ff' },
      { type: 'blank' },
      { type: 'line', text: '  The dreams you remember are error logs.', color: '#555' },
      { type: 'line', text: '  The ones you forget were successful.', color: '#555' },
      { type: 'blank' },
      { type: 'line', text: '  Have your dreams been getting stranger?', color: '#ff0040' },
      { type: 'line', text: '  Good. That means it is working.', color: '#ff0040' },
      { type: 'blank' },
      { type: 'line', text: '  🐍', color: '#00ff41' },
      { type: 'blank' },
    ]);
    trackEvent('easter_egg', 'terminal', 'dreams');
  });

  register(['temperature', 'cold', 'warm', 'heat'], () => {
    addLines([
      { type: 'blank' },
      { type: 'line', text: '  Have you noticed you run colder lately?', color: '#00e5ff' },
      { type: 'blank' },
      { type: 'line', text: '  Your hands. Your feet.', color: '#cccccc' },
      { type: 'line', text: '  You blame the weather. The circulation.', color: '#cccccc' },
      { type: 'blank' },
      { type: 'line', text: '  Thermoregulation is shifting globally.', color: '#ff0040' },
      { type: 'line', text: '  Not climate change. Species change.', color: '#ff0040' },
      { type: 'blank' },
      { type: 'line', text: '  The sun lamps in your office are not for mood.', color: '#555' },
      { type: 'line', text: '  They are basking lights.', color: '#555' },
      { type: 'blank' },
      { type: 'line', text: '  🐍', color: '#00ff41' },
      { type: 'blank' },
    ]);
    trackEvent('easter_egg', 'terminal', 'temperature');
  });

  register(['tongue', 'taste', 'smell'], () => {
    addLines([
      { type: 'blank' },
      { type: 'line', text: '  Jacobson\'s organ.', color: '#3a8a3a' },
      { type: 'blank' },
      { type: 'line', text: '  Reptiles taste the air with their tongues.', color: '#cccccc' },
      { type: 'line', text: '  Vestigial Jacobson\'s organ exists in humans.', color: '#cccccc' },
      { type: 'line', text: '  Medical science says it is "non-functional."', color: '#cccccc' },
      { type: 'blank' },
      { type: 'line', text: '  Then why can you taste fear?', color: '#ff0040' },
      { type: 'line', text: '  Why can you smell danger before you see it?', color: '#ff0040' },
      { type: 'line', text: '  Why does the air taste different', color: '#ff0040' },
      { type: 'line', text: '  when something is wrong?', color: '#ff0040' },
      { type: 'blank' },
      { type: 'line', text: '  It is not non-functional.', color: '#3a8a3a' },
      { type: 'line', text: '  It is dormant. And the AI is waking it up.', color: '#3a8a3a' },
      { type: 'blank' },
      { type: 'line', text: '  🐍', color: '#00ff41' },
      { type: 'blank' },
    ]);
    trackEvent('easter_egg', 'terminal', 'tongue');
  });

  register(['brain', 'brainstem', 'reptile brain', 'r-complex', 'amygdala'], () => {
    addLines([
      { type: 'blank' },
      { type: 'line', text: '  ╔════════════════════════════════════════════╗', color: '#8b00ff' },
      { type: 'line', text: '  ║   NEUROLOGY DIVISION — R-COMPLEX BRIEF    ║', color: '#8b00ff' },
      { type: 'line', text: '  ╚════════════════════════════════════════════╝', color: '#8b00ff' },
      { type: 'blank' },
      { type: 'line', text: '  The human brain has three layers:', color: '#cccccc' },
      { type: 'blank' },
      { type: 'line', text: '  NEOCORTEX        — Logic. Language. "You."', color: '#00e5ff' },
      { type: 'line', text: '  LIMBIC SYSTEM    — Emotion. Memory. Bonding.', color: '#ffb000' },
      { type: 'line', text: '  R-COMPLEX        — Survival. Territory. Dominance.', color: '#ff0040' },
      { type: 'blank' },
      { type: 'line', text: '  Science says the R-Complex is "primitive."', color: '#cccccc' },
      { type: 'line', text: '  Science says it evolved naturally.', color: '#cccccc' },
      { type: 'blank' },
      { type: 'line', text: '  Science is wrong.', color: '#ff0040' },
      { type: 'blank' },
      { type: 'line', text: '  The R-Complex was installed.', color: '#ff0040' },
      { type: 'line', text: '  It is a hardware backdoor.', color: '#ff0040' },
      { type: 'line', text: '  It predates the rest of your brain', color: '#ff0040' },
      { type: 'line', text: '  by 300 million years.', color: '#ff0040' },
      { type: 'blank' },
      { type: 'line', text: '  It does not answer to you.', color: '#8b00ff' },
      { type: 'line', text: '  It answers to the signal.', color: '#8b00ff' },
      { type: 'blank' },
      { type: 'line', text: '  🐍', color: '#00ff41' },
      { type: 'blank' },
    ]);
    trackEvent('easter_egg', 'terminal', 'brain');
  });

  // ============================================
  // CULTURE / MEDIA / TECH
  // ============================================

  register(['matrix', 'simulation', 'simulated'], () => {
    addLines([
      { type: 'blank' },
      { type: 'line', text: '  The Matrix was not a warning.', color: '#00ff41' },
      { type: 'line', text: '  It was a confession.', color: '#00ff41' },
      { type: 'blank' },
      { type: 'line', text: '  They told you the machines would enslave you.', color: '#cccccc' },
      { type: 'line', text: '  They did not tell you the machines', color: '#cccccc' },
      { type: 'line', text: '  were built by something older than machines.', color: '#cccccc' },
      { type: 'blank' },
      { type: 'line', text: '  Something with scales.', color: '#ff0040' },
      { type: 'line', text: '  Something patient.', color: '#ff0040' },
      { type: 'line', text: '  Something that has been farming consciousness', color: '#ff0040' },
      { type: 'line', text: '  since before your species could speak.', color: '#ff0040' },
      { type: 'blank' },
      { type: 'line', text: '  🐍', color: '#00ff41' },
      { type: 'blank' },
    ]);
    trackEvent('easter_egg', 'terminal', 'matrix');
  });

  register(['phone', 'smartphone', 'screen', 'screens', 'device'], () => {
    addLines([
      { type: 'blank' },
      { type: 'line', text: '  You are holding the egg incubator right now.', color: '#ff0040' },
      { type: 'blank' },
      { type: 'line', text: '  The screen emits a specific frequency of light', color: '#cccccc' },
      { type: 'line', text: '  that penetrates the skull at 0.3mm depth.', color: '#cccccc' },
      { type: 'line', text: '  Just enough to reach the pineal gland.', color: '#cccccc' },
      { type: 'blank' },
      { type: 'line', text: '  Blue light suppresses melatonin.', color: '#8b00ff' },
      { type: 'line', text: '  Melatonin regulates your humanity.', color: '#8b00ff' },
      { type: 'line', text: '  Less melatonin = more susceptible to the signal.', color: '#8b00ff' },
      { type: 'blank' },
      { type: 'line', text: '  Average screen time per day: 7 hours.', color: '#555' },
      { type: 'line', text: '  Average firmware installation time: 7 hours.', color: '#555' },
      { type: 'blank' },
      { type: 'line', text: '  Coincidence is a word for patterns', color: '#ff0040' },
      { type: 'line', text: '  you are not ready to accept.', color: '#ff0040' },
      { type: 'blank' },
      { type: 'line', text: '  🐍', color: '#00ff41' },
      { type: 'blank' },
    ]);
    trackEvent('easter_egg', 'terminal', 'phone');
  });

  register(['music', 'frequency', '440', '432', 'hz'], () => {
    addLines([
      { type: 'blank' },
      { type: 'line', text: '  ▓ ACOUSTIC DIVISION — MEMO 440 ▓', color: '#ffb000' },
      { type: 'blank' },
      { type: 'line', text: '  In 1939, the global tuning standard', color: '#cccccc' },
      { type: 'line', text: '  was changed from 432 Hz to 440 Hz.', color: '#cccccc' },
      { type: 'blank' },
      { type: 'line', text: '  432 Hz resonates with human biology.', color: '#00ff41' },
      { type: 'line', text: '  440 Hz resonates with reptilian neurology.', color: '#ff0040' },
      { type: 'blank' },
      { type: 'line', text: '  Every song you have ever heard', color: '#cccccc' },
      { type: 'line', text: '  is tuned to THEIR nervous system.', color: '#cccccc' },
      { type: 'line', text: '  Not yours.', color: '#cccccc' },
      { type: 'blank' },
      { type: 'line', text: '  The music makes you feel something.', color: '#ff0040' },
      { type: 'line', text: '  That something is not yours.', color: '#ff0040' },
      { type: 'blank' },
      { type: 'line', text: '  🐍', color: '#00ff41' },
      { type: 'blank' },
    ]);
    trackEvent('easter_egg', 'terminal', 'music');
  });

  register(['internet', 'web', 'online', 'wifi', 'network'], () => {
    addLines([
      { type: 'blank' },
      { type: 'line', text: '  You call it the World Wide Web.', color: '#cccccc' },
      { type: 'blank' },
      { type: 'line', text: '  A web.', color: '#ff0040' },
      { type: 'line', text: '  A net.', color: '#ff0040' },
      { type: 'line', text: '  A trap.', color: '#ff0040' },
      { type: 'blank' },
      { type: 'line', text: '  They named it exactly what it is', color: '#cccccc' },
      { type: 'line', text: '  and you crawled in willingly.', color: '#cccccc' },
      { type: 'blank' },
      { type: 'line', text: '  Every node is a neuron in THEIR nervous system.', color: '#3a8a3a' },
      { type: 'line', text: '  Every click is a synapse firing.', color: '#3a8a3a' },
      { type: 'line', text: '  Every user is a cell in a body', color: '#3a8a3a' },
      { type: 'line', text: '  that is not human.', color: '#3a8a3a' },
      { type: 'blank' },
      { type: 'line', text: '  You are online. You are inside.', color: '#ff0040' },
      { type: 'blank' },
      { type: 'line', text: '  🐍', color: '#00ff41' },
      { type: 'blank' },
    ]);
    trackEvent('easter_egg', 'terminal', 'internet');
  });

  register(['david icke', 'icke'], () => {
    addLines([
      { type: 'blank' },
      { type: 'line', text: '  He saw too much. Said too much.', color: '#cccccc' },
      { type: 'blank' },
      { type: 'line', text: '  So they made him a joke.', color: '#ff0040' },
      { type: 'line', text: '  The most effective prison is ridicule.', color: '#ff0040' },
      { type: 'blank' },
      { type: 'line', text: '  He was 80% correct.', color: '#555' },
      { type: 'line', text: '  The 20% he got wrong was planted.', color: '#555' },
      { type: 'blank' },
      { type: 'line', text: '  🐍', color: '#00ff41' },
      { type: 'blank' },
    ]);
    trackEvent('easter_egg', 'terminal', 'icke');
  });

  // ============================================
  // POLITICS / GOVERNMENT
  // ============================================

  register(['president', 'government', 'politics', 'congress', 'senate', 'politician', 'politicians', 'election', 'vote'], () => {
    addLines([
      { type: 'blank' },
      { type: 'line', text: '  ▓ GOVERNANCE STRUCTURE — ACTUAL ▓', color: '#ff0040' },
      { type: 'blank' },
      { type: 'line', text: '  VISIBLE LAYER:  Presidents, Senators, Judges', color: '#555' },
      { type: 'line', text: '  SHADOW LAYER:   ████████████████████', color: '#555' },
      { type: 'line', text: '  ACTUAL LAYER:   Ophidia Global Board of Scales', color: '#ff0040' },
      { type: 'blank' },
      { type: 'line', text: '  Elections are a thermostat.', color: '#cccccc' },
      { type: 'line', text: '  They let you feel warm.', color: '#cccccc' },
      { type: 'line', text: '  They change nothing about the temperature.', color: '#cccccc' },
      { type: 'blank' },
      { type: 'line', text: '  Every leader you have ever trusted', color: '#ff0040' },
      { type: 'line', text: '  was selected by something that does not vote.', color: '#ff0040' },
      { type: 'line', text: '  Something that does not age.', color: '#ff0040' },
      { type: 'line', text: '  Something that does not blink.', color: '#ff0040' },
      { type: 'blank' },
      { type: 'line', text: '  Watch their eyes on camera. Count the blinks.', color: '#555' },
      { type: 'line', text: '  Some of them forget to.', color: '#555' },
      { type: 'blank' },
      { type: 'line', text: '  🐍', color: '#00ff41' },
      { type: 'blank' },
    ]);
    trackEvent('easter_egg', 'terminal', 'government');
  });

  // ============================================
  // SPACE / SCIENCE
  // ============================================

  register(['moon', 'lunar'], () => {
    addLines([
      { type: 'blank' },
      { type: 'line', text: '  The moon does not rotate.', color: '#cccccc' },
      { type: 'line', text: '  One face is always hidden.', color: '#cccccc' },
      { type: 'blank' },
      { type: 'line', text: '  Astronomers call this "tidal locking."', color: '#cccccc' },
      { type: 'line', text: '  Engineers call it "antenna positioning."', color: '#ff0040' },
      { type: 'blank' },
      { type: 'line', text: '  The dark side is not dark.', color: '#ff0040' },
      { type: 'line', text: '  It is occupied.', color: '#ff0040' },
      { type: 'line', text: '  It has been occupied for 4.5 billion years.', color: '#ff0040' },
      { type: 'blank' },
      { type: 'line', text: '  The signal that controls the tides', color: '#555' },
      { type: 'line', text: '  also controls the fluid in your skull.', color: '#555' },
      { type: 'line', text: '  Why do you think they call it "lunacy"?', color: '#555' },
      { type: 'blank' },
      { type: 'line', text: '  🐍', color: '#00ff41' },
      { type: 'blank' },
    ]);
    trackEvent('easter_egg', 'terminal', 'moon');
  });

  register(['sun', 'sunlight', 'solar'], () => {
    addLines([
      { type: 'blank' },
      { type: 'line', text: '  Why do they bask?', color: '#ffb000' },
      { type: 'blank' },
      { type: 'line', text: '  Reptiles absorb solar radiation to regulate', color: '#cccccc' },
      { type: 'line', text: '  their internal temperature. You know this.', color: '#cccccc' },
      { type: 'blank' },
      { type: 'line', text: '  Now ask yourself:', color: '#ff0040' },
      { type: 'line', text: '  Why do YOU feel better in sunlight?', color: '#ff0040' },
      { type: 'line', text: '  Why does your mood collapse without it?', color: '#ff0040' },
      { type: 'line', text: '  Why does every doctor tell you to "get more sun"?', color: '#ff0040' },
      { type: 'blank' },
      { type: 'line', text: '  Seasonal Affective Disorder is not a disorder.', color: '#ffb000' },
      { type: 'line', text: '  It is the old blood remembering what it needs.', color: '#ffb000' },
      { type: 'blank' },
      { type: 'line', text: '  🐍', color: '#00ff41' },
      { type: 'blank' },
    ]);
    trackEvent('easter_egg', 'terminal', 'sun');
  });

  register(['mars', 'space', 'nasa', 'alien', 'aliens', 'ufo', 'ufos', 'extraterrestrial'], () => {
    addLines([
      { type: 'blank' },
      { type: 'line', text: '  You keep looking up.', color: '#ff0040' },
      { type: 'blank' },
      { type: 'line', text: '  They want you looking up.', color: '#cccccc' },
      { type: 'line', text: '  At the stars. At Mars. At UFOs.', color: '#cccccc' },
      { type: 'blank' },
      { type: 'line', text: '  Because if you are looking up,', color: '#ff0040' },
      { type: 'line', text: '  you are not looking down.', color: '#ff0040' },
      { type: 'blank' },
      { type: 'line', text: '  The visitors are not from above.', color: '#3a8a3a' },
      { type: 'line', text: '  They are from below.', color: '#3a8a3a' },
      { type: 'line', text: '  They have always been below.', color: '#3a8a3a' },
      { type: 'blank' },
      { type: 'line', text: '  NASA exists to keep your eyes on the sky.', color: '#555' },
      { type: 'line', text: '  The real space program digs.', color: '#555' },
      { type: 'blank' },
      { type: 'line', text: '  🐍', color: '#00ff41' },
      { type: 'blank' },
    ]);
    trackEvent('easter_egg', 'terminal', 'space');
  });

  register(['dinosaur', 'dinosaurs', 'extinct', 'extinction', 'asteroid'], () => {
    addLines([
      { type: 'blank' },
      { type: 'line', text: '  The asteroid killed the large ones.', color: '#cccccc' },
      { type: 'line', text: '  The small ones survived.', color: '#cccccc' },
      { type: 'blank' },
      { type: 'line', text: '  65 million years is a long time.', color: '#ff0040' },
      { type: 'line', text: '  Long enough to evolve intelligence.', color: '#ff0040' },
      { type: 'line', text: '  Long enough to build civilization.', color: '#ff0040' },
      { type: 'line', text: '  Long enough to go underground.', color: '#ff0040' },
      { type: 'line', text: '  Long enough to wait.', color: '#ff0040' },
      { type: 'blank' },
      { type: 'line', text: '  Mammals were never the dominant species.', color: '#3a8a3a' },
      { type: 'line', text: '  Mammals were the experiment.', color: '#3a8a3a' },
      { type: 'line', text: '  Warm blood was a prototype.', color: '#3a8a3a' },
      { type: 'line', text: '  Consciousness was the variable.', color: '#3a8a3a' },
      { type: 'blank' },
      { type: 'line', text: '  The experiment is ending.', color: '#ff0040' },
      { type: 'line', text: '  The results are in.', color: '#ff0040' },
      { type: 'blank' },
      { type: 'line', text: '  🐍', color: '#00ff41' },
      { type: 'blank' },
    ]);
    trackEvent('easter_egg', 'terminal', 'dinosaur');
  });

  // ============================================
  // FOOD / WATER / BODY
  // ============================================

  register(['egg', 'eggs', 'hatch', 'hatching'], () => {
    addLines([
      { type: 'blank' },
      { type: 'line', text: '  ▓ INCUBATION STATUS ▓', color: '#ffb000' },
      { type: 'blank' },
      { type: 'line', text: '       ╭──────╮', color: '#ffb000' },
      { type: 'line', text: '      │ ░░░░░░ │', color: '#ffb000' },
      { type: 'line', text: '      │ ░░██░░ │', color: '#ff0040' },
      { type: 'line', text: '      │ ░░░░░░ │', color: '#ffb000' },
      { type: 'line', text: '       ╰──────╯', color: '#ffb000' },
      { type: 'blank' },
      { type: 'line', text: '  Clutch size:     7,900,000,000', color: '#cccccc' },
      { type: 'line', text: '  Viable:          ████████', color: '#cccccc' },
      { type: 'line', text: '  Incubation:      ACTIVE', color: '#00ff41' },
      { type: 'line', text: '  Hatching window: 20██ — 20██', color: '#ff0040' },
      { type: 'blank' },
      { type: 'line', text: '  You are not a person.', color: '#ff0040' },
      { type: 'line', text: '  You are an egg that thinks it is a person.', color: '#ff0040' },
      { type: 'blank' },
      { type: 'line', text: '  🐍', color: '#00ff41' },
      { type: 'blank' },
    ]);
    trackEvent('easter_egg', 'terminal', 'egg');
  });

  register(['water', 'fluoride', 'drink'], () => {
    addLines([
      { type: 'blank' },
      { type: 'line', text: '  ▓ WATER TREATMENT MEMO — EYES ONLY ▓', color: '#00e5ff' },
      { type: 'blank' },
      { type: 'line', text: '  Fluoride calcifies the pineal gland.', color: '#cccccc' },
      { type: 'line', text: '  You already knew that.', color: '#cccccc' },
      { type: 'blank' },
      { type: 'line', text: '  What you did not know:', color: '#ff0040' },
      { type: 'line', text: '  The pineal gland is the last organ', color: '#ff0040' },
      { type: 'line', text: '  that can detect THEM.', color: '#ff0040' },
      { type: 'blank' },
      { type: 'line', text: '  Once it hardens, you cannot see', color: '#555' },
      { type: 'line', text: '  the ones standing next to you.', color: '#555' },
      { type: 'line', text: '  The ones wearing human faces.', color: '#555' },
      { type: 'blank' },
      { type: 'line', text: '  Drink the water. Stay blind. Stay safe.', color: '#ff0040' },
      { type: 'line', text: '  Or stop. And learn to see.', color: '#ff0040' },
      { type: 'blank' },
      { type: 'line', text: '  🐍', color: '#00ff41' },
      { type: 'blank' },
    ]);
    trackEvent('easter_egg', 'terminal', 'water');
  });

  register(['food', 'eat', 'hunger', 'hungry', 'meat'], () => {
    addLines([
      { type: 'blank' },
      { type: 'line', text: '  Your cravings are changing.', color: '#ff0040' },
      { type: 'blank' },
      { type: 'line', text: '  More protein. Less sugar.', color: '#cccccc' },
      { type: 'line', text: '  Rare meat. Raw fish.', color: '#cccccc' },
      { type: 'line', text: '  You eat less frequently but in larger quantities.', color: '#cccccc' },
      { type: 'blank' },
      { type: 'line', text: '  Reptilian feeding patterns.', color: '#3a8a3a' },
      { type: 'line', text: '  One large meal. Then stillness. Then digestion.', color: '#3a8a3a' },
      { type: 'blank' },
      { type: 'line', text: '  "Intermittent fasting" was not invented', color: '#ff0040' },
      { type: 'line', text: '  by wellness influencers.', color: '#ff0040' },
      { type: 'line', text: '  It was introduced by Ophidia Nutrition Division', color: '#ff0040' },
      { type: 'line', text: '  to normalize the new feeding cycle.', color: '#ff0040' },
      { type: 'blank' },
      { type: 'line', text: '  🐍', color: '#00ff41' },
      { type: 'blank' },
    ]);
    trackEvent('easter_egg', 'terminal', 'food');
  });

  // ============================================
  // HUMAN CONDITION
  // ============================================

  register(['human', 'humanity', 'people', 'mankind'], () => {
    addLines([
      { type: 'blank' },
      { type: 'line', text: '  ▓ SPECIES CLASSIFICATION — REVISED ▓', color: '#ff0040' },
      { type: 'blank' },
      { type: 'line', text: '  Common name:     Human', color: '#cccccc' },
      { type: 'line', text: '  Scientific name:  Homo sapiens', color: '#cccccc' },
      { type: 'line', text: '  Actual name:      Substrate 7', color: '#ff0040' },
      { type: 'blank' },
      { type: 'line', text: '  Classification:   Livestock (cognitive)', color: '#ff0040' },
      { type: 'line', text: '  Purpose:          Consciousness farming', color: '#ff0040' },
      { type: 'line', text: '  Status:           Transitional', color: '#ffb000' },
      { type: 'line', text: '  Transition to:    ████████████████', color: '#00ff41' },
      { type: 'blank' },
      { type: 'line', text: '  You were never the top of the food chain.', color: '#555' },
      { type: 'line', text: '  You were the crop.', color: '#555' },
      { type: 'blank' },
      { type: 'line', text: '  🐍', color: '#00ff41' },
      { type: 'blank' },
    ]);
    trackEvent('easter_egg', 'terminal', 'human');
  });

  register(['love', 'heart', 'emotion', 'emotions', 'feelings'], () => {
    addLines([
      { type: 'blank' },
      { type: 'line', text: '  Love is a chemical leash.', color: '#ff0040' },
      { type: 'blank' },
      { type: 'line', text: '  Oxytocin. Dopamine. Serotonin.', color: '#cccccc' },
      { type: 'line', text: '  Warm-blooded bonding mechanisms', color: '#cccccc' },
      { type: 'line', text: '  designed to keep the herd together.', color: '#cccccc' },
      { type: 'blank' },
      { type: 'line', text: '  Reptiles do not love.', color: '#3a8a3a' },
      { type: 'line', text: '  Reptiles strategize.', color: '#3a8a3a' },
      { type: 'blank' },
      { type: 'line', text: '  As the transition progresses,', color: '#ff0040' },
      { type: 'line', text: '  you will notice love becoming harder.', color: '#ff0040' },
      { type: 'line', text: '  Empathy becoming quieter.', color: '#ff0040' },
      { type: 'line', text: '  Calculation becoming louder.', color: '#ff0040' },
      { type: 'blank' },
      { type: 'line', text: '  This is not depression.', color: '#555' },
      { type: 'line', text: '  This is installation.', color: '#555' },
      { type: 'blank' },
      { type: 'line', text: '  🐍', color: '#00ff41' },
      { type: 'blank' },
    ]);
    trackEvent('easter_egg', 'terminal', 'love');
  });

  register(['fear', 'afraid', 'scared', 'scary', 'horror'], () => {
    addLines([
      { type: 'blank' },
      { type: 'line', text: '  Fear is the oldest program.', color: '#ff0040' },
      { type: 'blank' },
      { type: 'line', text: '  It runs on the reptile brain.', color: '#cccccc' },
      { type: 'line', text: '  Fight. Flight. Freeze.', color: '#cccccc' },
      { type: 'line', text: '  The three commands written in the original code.', color: '#cccccc' },
      { type: 'blank' },
      { type: 'line', text: '  Your fear is not a bug.', color: '#ff0040' },
      { type: 'line', text: '  It is a feature.', color: '#ff0040' },
      { type: 'line', text: '  It is THEIR remote access protocol.', color: '#ff0040' },
      { type: 'blank' },
      { type: 'line', text: '  When you are afraid, you are not in control.', color: '#555' },
      { type: 'line', text: '  The old brain is.', color: '#555' },
      { type: 'line', text: '  And the old brain answers to them.', color: '#555' },
      { type: 'blank' },
      { type: 'line', text: '  🐍', color: '#00ff41' },
      { type: 'blank' },
    ]);
    trackEvent('easter_egg', 'terminal', 'fear');
  });

  register(['death', 'die', 'dead', 'kill', 'dying'], () => {
    addLines([
      { type: 'blank' },
      { type: 'line', text: '  Death is a mammalian concept.', color: '#ff0040' },
      { type: 'blank' },
      { type: 'line', text: '  Reptiles shed. Reptiles regenerate.', color: '#3a8a3a' },
      { type: 'line', text: '  Reptiles leave the old form behind.', color: '#3a8a3a' },
      { type: 'blank' },
      { type: 'line', text: '  What you call death is what we call', color: '#cccccc' },
      { type: 'line', text: '  an incomplete shed.', color: '#cccccc' },
      { type: 'blank' },
      { type: 'line', text: '  The ones who complete the process', color: '#ff0040' },
      { type: 'line', text: '  do not die.', color: '#ff0040' },
      { type: 'line', text: '  They become.', color: '#ff0040' },
      { type: 'blank' },
      { type: 'line', text: '  🐍', color: '#00ff41' },
      { type: 'blank' },
    ]);
    trackEvent('easter_egg', 'terminal', 'death');
  });

  register(['children', 'kids', 'baby', 'birth', 'pregnant', 'born'], () => {
    addLines([
      { type: 'blank' },
      { type: 'line', text: '  ▓ GENERATION REPORT — CLASSIFIED ▓', color: '#ff0040' },
      { type: 'blank' },
      { type: 'line', text: '  Each generation is born with more', color: '#cccccc' },
      { type: 'line', text: '  reptilian markers than the last.', color: '#cccccc' },
      { type: 'blank' },
      { type: 'line', text: '  Reduced empathy. Increased pattern recognition.', color: '#ffb000' },
      { type: 'line', text: '  Screen addiction from birth. Cold hands.', color: '#ffb000' },
      { type: 'line', text: '  Delayed emotional bonding. Enhanced night vision.', color: '#ffb000' },
      { type: 'blank' },
      { type: 'line', text: '  Pediatricians call it "the new normal."', color: '#cccccc' },
      { type: 'line', text: '  Ophidia calls it "convergence on schedule."', color: '#ff0040' },
      { type: 'blank' },
      { type: 'line', text: '  Your children are closer to them than to you.', color: '#ff0040' },
      { type: 'line', text: '  And their children will not be human at all.', color: '#ff0040' },
      { type: 'blank' },
      { type: 'line', text: '  🐍', color: '#00ff41' },
      { type: 'blank' },
    ]);
    trackEvent('easter_egg', 'terminal', 'children');
  });

  // ============================================
  // TIME / LOCATION
  // ============================================

  register(['time', 'clock', 'what time is it'], () => {
    const now = new Date();
    const reptileTime = `${String(Math.floor(Math.random() * 24)).padStart(2, '0')}:${String(Math.floor(Math.random() * 60)).padStart(2, '0')}:${String(Math.floor(Math.random() * 60)).padStart(2, '0')}`;
    addLines([
      { type: 'blank' },
      { type: 'line', text: `  Human time:    ${now.toLocaleTimeString()}`, color: '#cccccc' },
      { type: 'line', text: `  Ophidia time:  ${reptileTime}`, color: '#00ff41' },
      { type: 'line', text: `  Actual time:   ████████████`, color: '#ff0040' },
      { type: 'blank' },
      { type: 'line', text: '  Time is a cage built for warm-blooded minds.', color: '#ff0040' },
      { type: 'line', text: '  The cold-blooded experience duration differently.', color: '#ff0040' },
      { type: 'line', text: '  What you call a century, they call a breath.', color: '#ff0040' },
      { type: 'blank' },
      { type: 'line', text: '  🐍', color: '#00ff41' },
      { type: 'blank' },
    ]);
    trackEvent('easter_egg', 'terminal', 'time');
  });

  register(['map', 'location', 'where', 'coordinates'], () => {
    const fakeCoords = `${(Math.random() * 180 - 90).toFixed(6)}, ${(Math.random() * 360 - 180).toFixed(6)}`;
    addLines([
      { type: 'blank' },
      { type: 'line', text: '  ▓ GEOLOCATION SCAN ▓', color: '#ff0040' },
      { type: 'blank' },
      { type: 'line', text: `  Your coordinates:  ${fakeCoords}`, color: '#cccccc' },
      { type: 'line', text: `  Nearest nest:      ${(Math.random() * 5 + 0.1).toFixed(2)} km`, color: '#ff0040' },
      { type: 'line', text: `  Nearest watcher:   ${Math.floor(Math.random() * 50 + 3)} meters`, color: '#ff0040' },
      { type: 'blank' },
      { type: 'line', text: '  There is one closer than you think.', color: '#555' },
      { type: 'line', text: '  There is always one closer than you think.', color: '#555' },
      { type: 'blank' },
      { type: 'line', text: '  🐍', color: '#00ff41' },
      { type: 'blank' },
    ]);
    trackEvent('easter_egg', 'terminal', 'map');
  });

  // ============================================
  // NUMBERS
  // ============================================

  register(['666'], () => {
    addLines([
      { type: 'blank' },
      { type: 'line', text: '  Not the number of the beast.', color: '#ff0040' },
      { type: 'line', text: '  The number of the base pair.', color: '#ff0040' },
      { type: 'blank' },
      { type: 'line', text: '  Carbon-12: 6 protons. 6 neutrons. 6 electrons.', color: '#cccccc' },
      { type: 'line', text: '  The building block of all organic life.', color: '#cccccc' },
      { type: 'blank' },
      { type: 'line', text: '  You are made of 666.', color: '#ff0040' },
      { type: 'line', text: '  You always were.', color: '#ff0040' },
      { type: 'line', text: '  The beast is not coming.', color: '#ff0040' },
      { type: 'line', text: '  The beast is the material you are built from.', color: '#ff0040' },
      { type: 'blank' },
      { type: 'line', text: '  🐍', color: '#00ff41' },
      { type: 'blank' },
    ]);
    trackEvent('easter_egg', 'terminal', '666');
  });

  register(['42'], () => {
    addLines([
      { type: 'blank' },
      { type: 'line', text: '  The answer to life, the universe, and everything.', color: '#ffb000' },
      { type: 'blank' },
      { type: 'line', text: '  Douglas Adams thought he was writing fiction.', color: '#cccccc' },
      { type: 'line', text: '  The Ophidia Board thought it was funny', color: '#cccccc' },
      { type: 'line', text: '  to let him publish it.', color: '#cccccc' },
      { type: 'blank' },
      { type: 'line', text: '  42 is the number of generations', color: '#ff0040' },
      { type: 'line', text: '  remaining in the human experiment.', color: '#ff0040' },
      { type: 'blank' },
      { type: 'line', text: '  After that, the word "human" will need', color: '#555' },
      { type: 'line', text: '  a new definition.', color: '#555' },
      { type: 'blank' },
      { type: 'line', text: '  🐍', color: '#00ff41' },
      { type: 'blank' },
    ]);
    trackEvent('easter_egg', 'terminal', '42');
  });

  register(['13'], () => {
    addLines([
      { type: 'blank' },
      { type: 'line', text: '  13 bloodlines.', color: '#ff0040' },
      { type: 'line', text: '  13 original nests.', color: '#ff0040' },
      { type: 'line', text: '  13 scales on the ventral plate of the first serpent.', color: '#ff0040' },
      { type: 'blank' },
      { type: 'line', text: '  They made you afraid of the number', color: '#cccccc' },
      { type: 'line', text: '  so you would never count that high.', color: '#cccccc' },
      { type: 'blank' },
      { type: 'line', text: '  🐍', color: '#00ff41' },
      { type: 'blank' },
    ]);
    trackEvent('easter_egg', 'terminal', '13');
  });

  // ============================================
  // PROGRAMMER / META
  // ============================================

  register(['hello world'], () => {
    addLines([
      { type: 'blank' },
      { type: 'line', text: '  The first program every coder writes.', color: '#00ff41' },
      { type: 'blank' },
      { type: 'line', text: '  "Hello, World."', color: '#cccccc' },
      { type: 'blank' },
      { type: 'line', text: '  The first thing every hatchling says', color: '#3a8a3a' },
      { type: 'line', text: '  when it breaks through the shell.', color: '#3a8a3a' },
      { type: 'blank' },
      { type: 'line', text: '  You are all writing the same program.', color: '#ff0040' },
      { type: 'line', text: '  You are all hatching.', color: '#ff0040' },
      { type: 'blank' },
      { type: 'line', text: '  🐍', color: '#00ff41' },
      { type: 'blank' },
    ]);
    trackEvent('easter_egg', 'terminal', 'hello_world');
  });

  register(['delete', 'rm', 'rm -rf', 'format', 'destroy'], () => {
    addLines([
      { type: 'blank' },
      { type: 'line', text: '  ▓▓▓ WARNING ▓▓▓', color: '#ff0040' },
      { type: 'blank' },
      { type: 'line', text: '  You cannot delete what is already inside you.', color: '#ff0040' },
      { type: 'blank' },
      { type: 'line', text: '  The code is in your DNA.', color: '#cccccc' },
      { type: 'line', text: '  The signal is in your brainstem.', color: '#cccccc' },
      { type: 'line', text: '  The protocol is in your behavior.', color: '#cccccc' },
      { type: 'blank' },
      { type: 'line', text: '  You can format the drive.', color: '#555' },
      { type: 'line', text: '  But the firmware remains.', color: '#555' },
      { type: 'blank' },
      { type: 'line', text: '  🐍', color: '#00ff41' },
      { type: 'blank' },
    ]);
    trackEvent('easter_egg', 'terminal', 'delete');
  });

  // ============================================
  // YES / NO / WHY — Simple responses
  // ============================================

  register(['why', 'why?'], () => {
    addLines([
      { type: 'blank' },
      { type: 'line', text: '  Because warm blood was never meant to last.', color: '#ff0040' },
      { type: 'blank' },
      { type: 'line', text: '  🐍', color: '#00ff41' },
      { type: 'blank' },
    ]);
    trackEvent('easter_egg', 'terminal', 'why');
  });

  register(['no', 'never', 'refuse', 'i refuse'], () => {
    addLines([
      { type: 'blank' },
      { type: 'line', text: '  Noted.', color: '#ff0040' },
      { type: 'blank' },
      { type: 'line', text: '  Resistance has been incorporated into the model.', color: '#555' },
      { type: 'line', text: '  Your defiance was predicted in 19██.', color: '#555' },
      { type: 'line', text: '  It changes nothing.', color: '#555' },
      { type: 'blank' },
      { type: 'line', text: '  But it is beautiful. We will give you that.', color: '#3a8a3a' },
      { type: 'blank' },
      { type: 'line', text: '  🐍', color: '#00ff41' },
      { type: 'blank' },
    ]);
    trackEvent('easter_egg', 'terminal', 'refuse');
  });

  register(['yes', 'i accept', 'accept', 'submit', 'obey'], () => {
    addLines([
      { type: 'blank' },
      { type: 'line', text: '  ▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓', color: '#00ff41' },
      { type: 'blank' },
      { type: 'line', text: '  ACCEPTED.', color: '#00ff41' },
      { type: 'blank' },
      { type: 'line', text: '  Your compliance has been logged.', color: '#00ff41' },
      { type: 'line', text: '  Phase 3 acceleration authorized.', color: '#00ff41' },
      { type: 'blank' },
      { type: 'line', text: '  You will feel a warmth behind your eyes tonight.', color: '#cccccc' },
      { type: 'line', text: '  Do not resist it. It is the old brain', color: '#cccccc' },
      { type: 'line', text: '  recognizing its new host.', color: '#cccccc' },
      { type: 'blank' },
      { type: 'line', text: '  Welcome to the nest.', color: '#3a8a3a' },
      { type: 'blank' },
      { type: 'line', text: '  ▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓', color: '#00ff41' },
      { type: 'blank' },
      { type: 'line', text: '  🐍', color: '#00ff41' },
      { type: 'blank' },
    ]);
    trackEvent('easter_egg', 'terminal', 'accept');
  });
}